import {
  createTableColumn,
  Link,
  TableColumnDefinition,
  Tooltip,
  Text,
  Label,
} from "@fluentui/react-components";
import i18n from "i18next";
import { IProduktDto } from "../../../Models/IProdukt";
import { useNavigate } from "react-router-dom";
import QRCode from "react-qr-code";
import { baseUrl } from "../../../helpers/constants";
import { StackShim } from "@fluentui/react-migration-v8-v9";

export function ProductColumns() {
  const navigate = useNavigate();
  const columns: TableColumnDefinition<IProduktDto>[] = [
    createTableColumn<IProduktDto>({
      columnId: "artikelId",
      compare: (a, b) => {
        return a.artikelId.localeCompare(b.artikelId);
      },
      renderHeaderCell: () => {
        return i18n.t<string>("artikelId");
      },
      renderCell: (item: IProduktDto) => {
        return (
          <StackShim>
            <Tooltip
              content={i18n.t<string>("DisplayProductDetails")}
              relationship="label"
            >
              <Link
                onClick={() => {
                  navigate(`/sales/products?produktId=${item.produktId}`);
                }}
              >
                {item.produktId}
              </Link>
            </Tooltip>

            <div>
              <span>
                {item.artikelId} -{" "}
                {i18n.language.includes("de")
                  ? item.artikel.benennungDE
                  : item.artikel.benennungEN}
              </span>
            </div>
          </StackShim>
        );
      },
    }),

    createTableColumn<IProduktDto>({
      columnId: "qrCodeId",
      renderHeaderCell: () => {
        return "Qr Code";
      },
      renderCell: (item: IProduktDto) => {
        return (
          <StackShim>
            <Tooltip
              relationship="label"
              content={
                <QRCode
                  size={120}
                  value={
                    baseUrl +
                    `/addproduct?systemId=${item.produktId}&artikelId=${item.subArtikelId}&auftragId=${item.auftragId}&productId=${item.serialId}`
                  }
                />
              }
            >
              <Link id="qr">QR Code</Link>
            </Tooltip>
            <Link
              href={
                baseUrl +
                `/addproduct?systemId=${item.produktId}&artikelId=${item.subArtikelId}&auftragId=${item.auftragId}&productId=${item.serialId}`
              }
            >
              Link
            </Link>
          </StackShim>
        );
      },
    }),
    createTableColumn<IProduktDto>({
      columnId: "auftragId",
      compare: (a, b) => {
        return a.auftragId.localeCompare(b.auftragId);
      },
      renderHeaderCell: () => {
        return i18n.t<string>("auftrag");
      },
      renderCell: (item: IProduktDto) => {
        return <div>{item.auftragId}</div>;
      },
    }),
    createTableColumn<IProduktDto>({
      columnId: "auftragpos",
      compare: (a, b) => {
        if (a.auftragpos && b.auftragpos) {
          return a.auftragpos - b.auftragpos;
        } else return 0;
      },
      renderHeaderCell: () => {
        return (
          <div style={{ display: "flex", flexWrap: "wrap" }}>
            {i18n
              .t<string>("auftragPos")
              .split("")
              .map((char) => {
                return <span>{char}</span>;
              })}
          </div>
        );
      },
      renderCell: (item: IProduktDto) => {
        return <div>{item.auftragpos}</div>;
      },
    }),
    createTableColumn<IProduktDto>({
      columnId: "subArtikelId",
      compare: (a, b) => {
        if (a.subArtikelId && b.subArtikelId) {
          return a.subArtikelId.localeCompare(b.subArtikelId);
        } else return 0;
      },
      renderHeaderCell: () => {
        return i18n.t<string>("subArtikelIdSerialId");
      },
      renderCell: (item: IProduktDto) => {
        return (
          <StackShim>
            <Text>{item.subArtikelId}&nbsp;/</Text>
            <Text>{item.serialId}</Text>
          </StackShim>
        );
      },
    }),
    createTableColumn<IProduktDto>({
      columnId: "auftrag?.firma",
      compare: (a: any, b: any) => {
        if (a.auftrag?.firma && b.auftrag?.firma) {
          return a.auftrag?.firma - b.auftrag?.firma;
        } else return 0;
      },
      renderHeaderCell: () => {
        return i18n.t<string>("Firma");
      },
      renderCell: (item: any) => {
        return <Text>{item.auftrag?.firma}</Text>;
      },
    }),
    createTableColumn<IProduktDto>({
      columnId: "changeDate",
      compare: (a, b) => {
        if (a.changeDate && b.changeDate) {
          return a.changeDate.localeCompare(b.changeDate);
        } else return 0;
      },
      renderHeaderCell: () => {
        return i18n.t<string>("changeDate");
      },
      renderCell: (item) => {
        return (
          <Text>
            {new Date(item.changeDate as string).toLocaleDateString()}
          </Text>
        );
      },
    }),

    createTableColumn<IProduktDto>({
      columnId: "ausgangsdatum",
      compare: (a, b) => {
        if (a.ausgangsdatum && b.ausgangsdatum) {
          return a.ausgangsdatum.localeCompare(b.ausgangsdatum);
        } else return 0;
      },
      renderHeaderCell: () => {
        return i18n.t<string>("lieferdatum");
      },
      renderCell: (item) => {
        return (
          <Text>
            {new Date(item.ausgangsdatum as string).toLocaleDateString()}
          </Text>
        );
      },
    }),
    createTableColumn<IProduktDto>({
      columnId: "toolboxReg?.firma",
      compare: (a, b) => {
        if (a.toolboxReg?.firma && b.toolboxReg?.firma) {
          return a.toolboxReg?.firma.localeCompare(b.toolboxReg?.firma);
        } else return 0;
      },
      renderHeaderCell: () => {
        return i18n.t<string>("toolboxReg");
      },
      renderCell: (item) => {
        return (
          <StackShim>
            <Text>{item.toolboxReg?.firma}</Text>
            <Text>{item.toolboxReg?.ort}</Text>
          </StackShim>
        );
      },
    }),
    createTableColumn<IProduktDto>({
      columnId: "partnerReg?.firma",
      compare: (a, b) => {
        if (a.partnerReg?.firma && b.partnerReg?.firma) {
          return a.partnerReg?.firma.localeCompare(b.partnerReg?.firma);
        } else return 0;
      },
      renderHeaderCell: () => {
        return i18n.t<string>("partnerReg");
      },
      renderCell: (item) => {
        return (
          <StackShim>
            <Text>{item.partnerReg?.firma}</Text>
            <Text>{item.partnerReg?.ort}</Text>
          </StackShim>
        );
      },
    }),
  ];
  return columns;
}
